import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useRef, useEffect } from 'react';
// @ts-expect-error shaka player lacks typing https://github.com/shaka-project/shaka-player/issues/1030
import shaka from 'shaka-player/dist/shaka-player.compiled.debug';
import { CaretRightOutlined, PauseOutlined, SoundOutlined } from '@ant-design/icons';
import { emitter, getCaptionsBegin, getCaptionsEnd, getCurrentCaptions, } from '../../live-captions';
import './style.less';
const SLIDER_RANGE = {
    min: 0,
    max: 10000,
};
const formatTime = (secs) => {
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60);
    const hours = minutes > 59 ? Math.floor(minutes / 60).toString() : '0';
    return `${hours.padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};
const MediaPlayer = ({ projectId }) => {
    const [playerState, setPlayerState] = useState({
        captions: { begin: 0, end: 0, text: '' },
        timePosition: 0,
        isSeeking: false,
        resumeAfterSeek: false,
    });
    const [totalDuration, setTotalDuration] = useState(0);
    const videoElement = useRef(null);
    const sliderElement = useRef(null);
    const { host, protocol } = window.location;
    useEffect(() => {
        setTotalDuration(getCaptionsEnd());
        const loadPlayer = async () => {
            const shakaPlayer = new shaka.Player(videoElement.current);
            window.player = shakaPlayer;
            shaka.polyfill.installAll();
            shakaPlayer.attach(videoElement.current);
            await window.player.load(`${protocol}//${host}/XAPI/v2/Projects/${projectId}/Files/MpdManifest`, 0, 'application/dash+xml');
        };
        void loadPlayer();
        if (videoElement.current !== null) {
            videoElement.current.currentTime = getCaptionsBegin();
        }
        emitter.on('newCaption', () => setTotalDuration(getCaptionsEnd()));
    }, []);
    const handleTimeUpdate = () => {
        if (videoElement.current !== null) {
            const currentCaptions = getCurrentCaptions(videoElement.current.currentTime);
            if (sliderElement.current !== null) {
                setPlayerState(Object.assign(Object.assign({}, playerState), { captions: currentCaptions.length > 0 ? currentCaptions[0] : { begin: 0, end: 0, text: '' }, timePosition: (videoElement.current.currentTime * SLIDER_RANGE.max) / totalDuration }));
                sliderElement.current.style.backgroundSize = `${(videoElement.current.currentTime * 100) / totalDuration}%`;
                if (totalDuration < videoElement.current.currentTime) {
                    videoElement.current.pause();
                }
            }
        }
    };
    const handleSeek = (e) => {
        setPlayerState(Object.assign(Object.assign({}, playerState), { timePosition: (Number(e.target.value)) }));
        if (sliderElement.current !== null) {
            sliderElement.current.style.backgroundSize = `${Number(e.target.value) / 100}%`;
        }
    };
    const handleVolumeChange = (e) => {
        if (videoElement.current !== null) {
            videoElement.current.volume = Number(e.target.value) / 100;
        }
        e.target.style.backgroundSize = `${e.target.value}% 100%`;
    };
    const handleMouseDown = () => {
        var _a;
        const resumeAfterSeek = videoElement.current !== null && !videoElement.current.paused;
        setPlayerState(Object.assign(Object.assign({}, playerState), { resumeAfterSeek, isSeeking: true }));
        (_a = videoElement.current) === null || _a === void 0 ? void 0 : _a.pause();
    };
    const handleMouseUp = () => {
        var _a;
        setPlayerState(Object.assign(Object.assign({}, playerState), { isSeeking: false }));
        if (videoElement.current !== null) {
            videoElement.current.currentTime = (totalDuration * playerState.timePosition)
                / SLIDER_RANGE.max;
        }
        if (playerState.resumeAfterSeek) {
            void ((_a = videoElement.current) === null || _a === void 0 ? void 0 : _a.play());
        }
    };
    const wrapCaptions = (captions) => {
        const [firstLine, secondLine] = captions.split('\n');
        return (_jsxs("div", { style: { minHeight: '4rem' }, children: [_jsx("div", { className: "media-player__captions", children: firstLine }), _jsx("div", { className: "media-player__captions", children: secondLine })] }));
    };
    return (_jsxs("div", { className: "media-player", children: [_jsx("div", { className: "media-player__video", children: _jsx("video", { autoPlay: true, ref: videoElement, onTimeUpdate: handleTimeUpdate, children: _jsx("track", { src: "", kind: "captions", label: "" }) }) }), _jsxs("div", { className: "media-player__controls", children: [videoElement.current !== null && videoElement.current.paused
                        ? _jsx("button", { type: "button", onClick: () => { var _a; return (_a = videoElement.current) === null || _a === void 0 ? void 0 : _a.play(); }, "aria-label": "Play", children: _jsx(CaretRightOutlined, {}) })
                        : _jsx("button", { type: "button", onClick: () => { var _a; return (_a = videoElement.current) === null || _a === void 0 ? void 0 : _a.pause(); }, "aria-label": "Pause", children: _jsx(PauseOutlined, {}) }), _jsxs("div", { className: "media-player__time", children: [_jsx("input", { ref: sliderElement, type: "range", min: SLIDER_RANGE.min, max: SLIDER_RANGE.max, onMouseDown: handleMouseDown, onMouseUp: handleMouseUp, onTouchStart: handleMouseDown, onTouchEnd: handleMouseUp, onInput: handleSeek, value: playerState.timePosition, className: "media-player__time-slider" }), _jsxs("div", { className: "media-player__time-info", children: [_jsx("span", { children: videoElement.current === null ? '00:00:00' : formatTime(videoElement.current.currentTime) }), _jsx("span", { children: videoElement.current === null ? '00:00:00' : formatTime(totalDuration) })] })] }), _jsxs("div", { className: "media-player__volume", children: [_jsx("input", { type: "range", min: "0", max: "100", onInput: handleVolumeChange, className: "media-player__volume-slider" }), _jsx(SoundOutlined, {})] })] }), wrapCaptions(playerState.captions.text)] }));
};
export default MediaPlayer;
